import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCookies } from 'react-cookie';
import { format, fromUnixTime } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useAppSelector } from 'stores/hooks';
import { getUserSettings, getUser, UserLocation } from '@surfline/web-common';
import {
  getLtaEntitlement,
  getLtaExpiresAt,
  getLtaType,
  getLtaShowBanner,
  getLtaBannerCopy,
  getHasLtaAccess,
  getIsLtaGlobal,
  getLtaSpotIds,
  getLtaSubregionIds,
  getCurrentSpotId,
  getCurrentSubregionId,
} from 'selectors/user';
import applyLtaBannerCopyPlaceholders, {
  DEFAULT_LTA_BANNER_COPY,
} from 'utils/applyLtaBannerCopyPlaceholders';
import { LtaBadgeIcon } from '../Icons';
import styles from './LtaCountdownBanner.module.scss';

export const LTA_COOKIE_PREFIX = 'sl_ltab';

const getTierDisplayName = (entitlement: string): string => {
  switch (entitlement) {
    case 'sl_premium_plus':
      return 'Premium+';
    case 'sl_premium':
      return 'Premium';
    case 'sl_premium_ads':
      return 'Premium with Ads';
    default:
      return 'Premium';
  }
};

const getTierColorClass = (entitlement: string): string => {
  switch (entitlement) {
    case 'sl_premium_plus':
      return styles.premiumPlusText;
    case 'sl_premium':
    case 'sl_premium_ads':
      return styles.premiumText;
    default:
      return styles.premiumText;
  }
};

const getBadgeVariant = (entitlement: string): 'premium_plus' | 'premium' => {
  return entitlement === 'sl_premium_plus' ? 'premium_plus' : 'premium';
};

const LtaCountdownBanner: React.FC = () => {
  const [cookies, setCookie] = useCookies();
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [isClient, setIsClient] = useState(false);

  const userSettings = useAppSelector(getUserSettings);
  const { location: userLocation } = useAppSelector(getUser) as { location: UserLocation };
  const userTimezone = userLocation?.time_zone || 'America/Los_Angeles';
  const dateFormat = userSettings?.date?.format ? userSettings.date.format : 'MDY';

  const ltaEntitlement = useAppSelector(getLtaEntitlement);
  // Scoped-access LTAs return entitlements like `sl_scoped_access_premium_plus` —
  // collapse to the plain tier string so display/cookie logic can stay tier-keyed.
  const displayEntitlement = ltaEntitlement?.replace('_scoped_access_', '_');
  const ltaExpiresAt = useAppSelector(getLtaExpiresAt);
  const ltaType = useAppSelector(getLtaType);
  const ltaShowBanner = useAppSelector(getLtaShowBanner);
  const ltaBannerCopy = useAppSelector(getLtaBannerCopy);
  const hasLtaAccess = useAppSelector(getHasLtaAccess);
  const isLtaGlobal = useAppSelector(getIsLtaGlobal);
  const ltaSpotIds = useAppSelector(getLtaSpotIds);
  const ltaSubregionIds = useAppSelector(getLtaSubregionIds);
  const currentSpotId = useAppSelector(getCurrentSpotId);
  const currentSubregionId = useAppSelector(getCurrentSubregionId);

  const isLtaCampaign = ltaType === 'lta';
  const isOnLtaSpot =
    isLtaGlobal ||
    (currentSpotId && ltaSpotIds.includes(currentSpotId)) ||
    (currentSubregionId && ltaSubregionIds.includes(currentSubregionId));

  // Create a campaign-specific cookie name using type, entitlement, and expiry
  const ltaCookieName = useMemo(() => {
    if (!ltaType || !displayEntitlement || !ltaExpiresAt) return null;
    return `${LTA_COOKIE_PREFIX}_${ltaType}_${displayEntitlement}_${ltaExpiresAt}`;
  }, [ltaType, displayEntitlement, ltaExpiresAt]);

  const formattedExpiryDate = useMemo(() => {
    if (!isClient || !ltaExpiresAt) return '';

    const formatMapping: { [key: string]: string } = {
      MDY: 'M/dd',
      DMY: 'dd/M',
      YMD: 'MM/dd',
    };
    const formatString = formatMapping[dateFormat] || 'M/dd';

    const expiryDate = fromUnixTime(ltaExpiresAt);
    const expiryDateZoned = toZonedTime(expiryDate, userTimezone);

    return format(expiryDateZoned, formatString);
  }, [isClient, ltaExpiresAt, dateFormat, userTimezone]);

  const tierDisplayName = displayEntitlement ? getTierDisplayName(displayEntitlement) : '';
  const tierColorClass = displayEntitlement ? getTierColorClass(displayEntitlement) : '';
  const badgeVariant = displayEntitlement ? getBadgeVariant(displayEntitlement) : 'premium';

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!hasLtaAccess || !isLtaCampaign || !isOnLtaSpot || !isClient || !ltaCookieName) return;

    const cookieValue = cookies[ltaCookieName];
    if (cookieValue !== 'closed') {
      setIsBannerVisible(true);
    }
  }, [cookies, hasLtaAccess, isLtaCampaign, isOnLtaSpot, isClient, ltaCookieName]);

  const handleClose = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (ltaExpiresAt && ltaCookieName) {
        const expiryDate = fromUnixTime(ltaExpiresAt);
        setCookie(ltaCookieName, 'closed', {
          path: '/',
          expires: expiryDate,
        });
        setIsBannerVisible(false);
      }
    },
    [ltaExpiresAt, ltaCookieName, setCookie],
  );

  const shouldShowBanner =
    hasLtaAccess &&
    isLtaCampaign &&
    ltaShowBanner &&
    isOnLtaSpot &&
    isBannerVisible &&
    isClient &&
    formattedExpiryDate;

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <Box className={styles.box} data-testid="lta-countdown-banner">
      <div className={styles.content}>
        <LtaBadgeIcon variant={badgeVariant} />
        <Typography className={styles.text} variant="body1">
          {applyLtaBannerCopyPlaceholders(ltaBannerCopy || DEFAULT_LTA_BANNER_COPY, {
            tier: <span className={tierColorClass}>{tierDisplayName}</span>,
            endDate: formattedExpiryDate,
          })}
        </Typography>
      </div>
      <IconButton className={styles.closeButton} onClick={handleClose} data-testid="close-button">
        <CloseIcon />
      </IconButton>
    </Box>
  );
};

export default LtaCountdownBanner;
